import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ScheduleTag,
  Timetable,
  TimetableDayOfTheWeek,
} from '@data/fms/schedule/types';
import { useAtomCallback } from 'jotai/utils';

export type PostTimetableTask = {
  task_type: 'move';
  origin_point?: number;
  destination_point?: number;
  partial_route_ids?: string[];
  plan_start_hm: string;
  description?: string;
};

export type PostTimetableBody = {
  area_map_version_id?: string;
  days_of_the_week: TimetableDayOfTheWeek[];
  timezone?: string;
  tasks: PostTimetableTask[];
  tags?: ScheduleTag[];
};

/**
 * 運行ダイヤスケジュール登録API
 */
export const usePostTimetablesAPI = (): {
  state: APIState;
  postTimetables: (
    vehicleId: string,
    body: PostTimetableBody,
  ) => Promise<Timetable[]>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();
  const fmsAPI = useFMSAPI();

  const postTimetablesAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      body: PostTimetableBody,
    ): Promise<Result<Timetable[], AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/timetables`,
          body,
        );
        return new Success(res?.data?.timetables);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postTimetables = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, body: PostTimetableBody) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_time_tables', { status: 'failed' }),
          });
          setState('hasError');
          return [];
        }
        setState('loading');
        // タグに app_name を追加
        if (!isNullOrUndefined(body.tags) && Array.isArray(body.tags)) {
          body = {
            ...body,
            tags: [...body.tags, { key: 'app_name', value: 'FMS Console' }],
          };
        } else {
          body = {
            ...body,
            tags: [{ key: 'app_name', value: 'FMS Console' }],
          };
        }
        const res = await postTimetablesAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          body,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_time_tables', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return [];
        }
        notifySuccess({
          message: t('api.fms.post_time_tables', { status: 'successfully' }),
          testIdAttrs: {
            wrapperTestId: 'createTimetable-snackbar',
            closeButtonTestId: 'createTimetable-snackbar-close-btn',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, postTimetablesAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postTimetables,
  };
};
